import React, {
  FC,
  Fragment,
  MutableRefObject,
  useCallback,
  useContext,
  useRef,
} from "react";
import { Pagination, Table } from "rsuite";
import { useTableSize } from "../../../../hook/width";
import { TableInstance } from "rsuite/Table";
import { UserIncident } from "../../../../domain/incident/Incident";
import { TableColumnContext } from "../../../context/TableContext";
import { IncidentTableTimestampCell } from "./timestamp";
import { IncidentTableUnityCell } from "./unity";
import { IncidentTableZoneCell } from "./zone";
import { IncidentTableActionCell } from "./action";
import { faCalendarXmark } from "@fortawesome/free-solid-svg-icons";
import { EmptyState } from "../../../components/state/empty";
import { useDataPager } from "../../../../hook/pager";
import { IncidentTableColumns } from "../../../../schema/table/IncidentTable";
import { useStyleContext } from "../../../context/StyleContext";

type Props = {
  userIncidentList?: UserIncident[];
  name: string;
  onPreviewClicked: (item: UserIncident) => void;
  onSelect: (item: UserIncident) => void;
};

export const IncidentTable: FC<Props> = ({
  userIncidentList,
  name,
  onPreviewClicked,
  onSelect,
}) => {
  const actionRef = useRef<string | null>(null) as MutableRefObject<
    string | null
  >;
  const tableRef = useRef<TableInstance<any, any>>(null);
  const tableSize = useTableSize(tableRef);
  const tableContext = useContext(TableColumnContext);
  const tablePreferences =
    tableContext.getTablePreferences<IncidentTableColumns>(name);
  const { tableHeaderStyleSecondary } = useStyleContext();

  const { partialData, changePage, page, changeLimit, limits, limit } =
    useDataPager({
      id: `inc_table_${name}`,
      items: userIncidentList,
    });

  const onRowSelect = useCallback(
    (item: UserIncident) => {
      if (actionRef.current) onPreviewClicked(item);
      else onSelect(item);
      actionRef.current = null;
    },
    [partialData]
  );

  const onPreviewActionSelected = useCallback(() => {
    actionRef.current = "preview";
  }, [partialData]);

  return (
    <Fragment>
      <Table
        className={"rounded md:rounded-xl"}
        autoHeight
        data={partialData}
        ref={tableRef}
        renderEmpty={() => (
          <EmptyState
            icon={faCalendarXmark}
            title={"No hay incidencias"}
            message={
              "El usuario no reportó incidencias en la fecha seleccionada."
            }
          />
        )}
        onRowClick={onRowSelect as any}
        cellBordered
      >
        {tablePreferences.map((column) => {
          switch (column.name) {
            case "timestamp":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(column.weight)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<IncidentTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Publicación
                  </Table.HeaderCell>
                  <IncidentTableTimestampCell />
                </Table.Column>
              );
            case "date":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(column.weight)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<IncidentTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Día
                  </Table.HeaderCell>
                  <Table.Cell dataKey={"date"} />
                </Table.Column>
              );
            case "time":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(column.weight)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<IncidentTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Hora
                  </Table.HeaderCell>
                  <Table.Cell dataKey={"time"} />
                </Table.Column>
              );
            case "type":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(column.weight)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<IncidentTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Tipo
                  </Table.HeaderCell>
                  <Table.Cell dataKey={"type"} />
                </Table.Column>
              );
            case "unity":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(column.weight)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<IncidentTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Unidad
                  </Table.HeaderCell>
                  <IncidentTableUnityCell />
                </Table.Column>
              );
            case "zone":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(column.weight)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<IncidentTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Zona
                  </Table.HeaderCell>
                  <IncidentTableZoneCell />
                </Table.Column>
              );
            default:
              return (
                <Table.Column
                  align={"center"}
                  verticalAlign={"middle"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(column.weight)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<IncidentTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Acciones
                  </Table.HeaderCell>
                  <IncidentTableActionCell
                    onPreviewActionClicked={onPreviewActionSelected}
                  />
                </Table.Column>
              );
          }
        })}
      </Table>

      {userIncidentList ? (
        <div className={"py-4 flex flex-col gap-4"}>
          <span className={"text-md"}>
            Mostrando {partialData?.length} elementos / Total{" "}
            {userIncidentList.length}
          </span>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["limit", "|", "pager", "skip"]}
            total={userIncidentList.length}
            limitOptions={limits}
            limit={limit}
            activePage={page}
            onChangePage={changePage}
            onChangeLimit={changeLimit}
          />
        </div>
      ) : null}
    </Fragment>
  );
};
